/**
 * Vuex module for managing user-related state and actions.
 *
 * This module provides a centralized place to manage user-related data, including
 * user profile fields, validation, and terms acceptance. It exposes actions and
 * getters to interact with this state.
 */
export default {
  namespaced: true,


  /**
   * Defines the user-related state for the Vuex store module.
   *
   * This state includes various user profile fields, such as first name, surname,
   * telephone, email, company name, FCA number, and TPSP internal ID. Each field
   * has properties like value, label, type, error message, CSS class, validation
   * status, and a validation function. The state also includes an additional
   * information field and a terms acceptance field.
   */
  state: {
    firstname: {
      value: "",
      label: "First Name",
      type: "text",
      errorMessage: "First name is required",
      class: "l2",
      isValid: false,
      showValidation: false,
      validate: (value) => value.trim() !== "",
      showField: true,
    },
    surname: {
      value: "",
      label: "Surname",
      type: "text",
      errorMessage: "Surname is required",
      class: "last",
      isValid: false,
      showValidation: false,
      validate: (value) => value.trim() !== "",
      showField: true,
    },
    telephone: {
      value: "",
      label: "Telephone",
      type: "tel",
      errorMessage: "Telephone is required",
      class: "l2",
      isValid: false,
      showValidation: false,
      validate: (value) => value.trim() !== "",
      showField: true,
    },
    email: {
      value: "",
      label: "Email",
      type: "email",
      errorMessage: "Valid email is required",
      class: "last",
      isValid: false,
      showValidation: false,
      validate: (value) => {
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        return emailRegex.test(value.trim());
      },
      showField: true,
    },
    company_name: {
      value: "",
      label: "Company Name",
      type: "text",
      errorMessage: "Company name is required",
      class: "l2",
      isValid: false,
      showValidation: false,
      validate: (value) => value.trim() !== "",
      showField: true,
    },
    fca_number: {
      value: "",
      label: "FCA Number (one form per FCA number)",
      type: "text",
      errorMessage: "Please enter one valid FCA number",
      class: "last",
      isValid: false,
      showValidation: false,
      validate: (value) => {
        const trimmedValue = value.trim();
        // Check if it's empty
        if (trimmedValue === "") return false;
        // Check if it's numeric and exactly 6 or 7 characters
        const numericRegex = /^\d{6,7}$/;
        return numericRegex.test(trimmedValue);
      },
      showField: true,
    },
    tpsp_internal_id: {
      value: "",
      label: "TPSP Internal Reference Number",
      type: "text",
      errorMessage: "TPSP Internal ID is required",
      class: "",
      isValid: true,
      showValidation: false,
      validate: (value) => value.trim() !== "",
      showField: false,
    },
    additional_information: {
      value: "",
    },
    terms: {
      value: false,
      required: true,
      showValidation: false,
    },
  },



  /**
   * Vuex mutations for updating the user state in the store.
   *
   * - `SET_USER_FIELD`: Updates the value of a specific user field.
   * - `SET_FIELD`: Updates multiple properties of a specific user field.
   * - `SET_FIELD_PROPERTY`: Updates a specific property of a user field.
   * - `SET_TERMS_SHOW_VALIDATION`: Sets the showValidation flag for the terms field.
   */
  mutations: {
    SET_USER_FIELD(state, { field, value }) {
      state[field].value = value;
    },
    SET_FIELD(state, { fieldName, updates }) {
      state[fieldName] = { ...state[fieldName], ...updates };
    },
    SET_FIELD_PROPERTY(state, { field, property, value }) {
      state[field][property] = value;
    },
    SET_TERMS_SHOW_VALIDATION(state, showValidation) {
      state.terms.showValidation = showValidation;
    },
  },



  /**
   * Vuex actions for updating the user state in the store.
   *
   * - `updateUser`: Updates the value of a specific user field and validates the field if a validation function is provided.
   * - `updateField`: Updates multiple properties of a specific user field.
   * - `updateFieldProperty`: Updates a specific property of a user field.
   * - `updateTermsAcceptance`: Updates the acceptance state of the terms field.
   * - `updateTermsShowValidation`: Sets the showValidation flag for the terms field.
   */
  actions: {
    updateUser({ commit, state }, { field, value }) {
      commit("SET_USER_FIELD", { field, value });
      if (state[field].validate) {
        const isValid = state[field].validate(value);
        commit("SET_FIELD_PROPERTY", {
          field,
          property: "isValid",
          value: isValid,
        });
      }
    },
    updateField({ commit }, { fieldName, updates }) {
      commit('SET_FIELD', { fieldName, updates });
    },
    updateFieldProperty({ commit }, { field, property, value }) {
      commit("SET_FIELD_PROPERTY", { field, property, value });
    },
    updateTermsAcceptance({ commit }, accepted) {
      commit("SET_USER_FIELD", { field: "terms", value: accepted });
    },
    updateTermsShowValidation({ commit }, showValidation) {
      commit("SET_TERMS_SHOW_VALIDATION", showValidation);
    },
    updateTpspInternalIdVisibility({ commit }, shouldShow) {
      commit('SET_FIELD_PROPERTY', {
        field: 'tpsp_internal_id',
        property: 'showField',
        value: shouldShow
      });
    },
  },


  /**
   * Vuex getters for accessing the user state in the store.
   *
   * - `getUser`: Returns an object containing the values of all user fields.
   * - `getUserField`: Returns the value of a specific user field.
   * - `getFieldProperties`: Returns the properties of a specific user field.
   * - `isTermsAccepted`: Returns the acceptance state of the terms field.
   * - `getTerms`: Returns the terms field object.
   * - `getFields`: Returns an array of the keys of all user fields that have a `showField` property.
   */
  getters: {
    getUser: (state) => {
      const user = {};
      Object.keys(state).forEach((key) => {
        user[key] = state[key].value;
      });
      return user;
    },
    getUserField: (state) => (field) => {
      return state[field].value;
    },
    getFieldProperties: (state) => (field) => {
      return state[field];
    },
    isTermsAccepted: (state) => state.terms.value,
    getTerms: (state) => state.terms,
    getFields: (state) => {
      return Object.keys(state).filter(
        (key) => state[key].showField !== undefined
      );
    },
  },
};
